import {Directive,Input,OnChanges} from "@angular/core";
import {AbstractControl,NG_VALIDATORS,ValidationErrors,Validator} from "@angular/forms";
import {Vehicule} from "@domain/vehicule/vehicule";
import {NiveauAutorisation} from "@domain/vehicule/autorisation";
import * as moment from "moment/moment";
import {SettingsNFState,SettingsODState} from "@domain/settings/settings";

/**
 * Validator permettant de vérifier si le véhicule sélectionné dispose d'une autorisation de circuler pour une date donnée
 *
 * @author Laurent Convert
 * @date 06/01/2026
 */
@Directive({
	selector: '[vehiculeAvecAutorisation]',
	providers: [{provide : NG_VALIDATORS, useExisting: VehiculeAvecAutorisationDirective, multi: true}],
	exportAs: 'vehiculeAvecAutorisation'
})
export class VehiculeAvecAutorisationDirective implements Validator,OnChanges {
	/**
	 * Input permettant de spécifier les paramètres de la directive
	 */
	@Input()
	vehiculeAvecAutorisation: {
		//Paramètres en fonction de la portée
		settings: SettingsODState | SettingsNFState,
		//Date pour laquelle vérifier l'autorisation de circuler
		date: Date
	};

	/**
	 * Statut de la directive
	 */
	public statutValidation: StatutValidation = null;

	/**
	 * Fonction de callback pour notifier Angular d'un changement de validateur
	 */
	private _onChange?: () => void;

	/**
	 * Enregistre la fonction de callback pour notifier Angular d'un changement de validateur
	 */
	registerOnValidatorChange(fn: () => void): void {
		this._onChange = fn;
	}

	/**
	 * Notifie Angular de revalider le contrôle quand l'input change
	 */
	ngOnChanges(): void {
		this._onChange?.();
	}

	/**
	 * Retourne le niveau de contrôle de l'autorisation de circuler en fonction du type de settings
	 */
	get niveauControleAutorisationVehicule(): NiveauAutorisation {
		const settings = this.vehiculeAvecAutorisation?.settings;

		return (settings as SettingsODState)?.niveauControleAutorisationVehiculeOd
			?? (settings as SettingsNFState)?.niveauControleAutorisationVehiculeNdf
			?? null;
	}

	/**
	 * Vérifie si le véhicule dispose d'une autorisation de circuler pour une date donnée
	 *
	 * @param control le contrôle à valider
	 */
	validate(control: AbstractControl): ValidationErrors | null {
		//Récupération du véhicule (qui correspond au ngModel)
		const vehicule: Vehicule = control.value;

		//Réinitialisation du statut de la directive
		let statutValidation: StatutValidation = null;

		//Vérification des prérequis :
		// - L'option est activée
		// - Le niveau de contrôle est actif (bloquant ou non bloquant)
		// - Le véhicule dispose d'au moins une autorisation
		// - La date est renseignée
		if (this.vehiculeAvecAutorisation?.settings?.isAutorisationVehiculeActif
			&& this.niveauControleAutorisationVehicule !== NiveauAutorisation.NON_CONTROLE
			&& vehicule?.listeAutorisation
			&& this.vehiculeAvecAutorisation.date
		) {
			//Recherche si le véhicule dispose d'une autorisation
			const hasAutorisationCirculer = vehicule.listeAutorisation.some(a =>
				moment(a.dateDebut).isSameOrBefore(this.vehiculeAvecAutorisation.date,'day')
				&& moment(a.dateFin).isSameOrAfter(this.vehiculeAvecAutorisation.date,'day')
			);

			//Si le véhicule n'a pas d'autorisation de circuler pour la date
			if (!hasAutorisationCirculer) {
				//Si le niveau de contrôle est bloquant
				if (this.niveauControleAutorisationVehicule === NiveauAutorisation.BLOQUANT) {
					statutValidation = 'ERROR';
				} else {
					//Si le niveau de contrôle est non bloquant
					statutValidation = 'WARNING';
				}
			}
		}

		//Mise à jour du statut de la directive
		this.statutValidation = statutValidation;

		//Si l'erreur est bloquante
		if (statutValidation === 'ERROR') {
			//Retourne une erreur pour que le validateur remonte l'erreur au formulaire
			return {'vehiculeAvecAutorisation': true};
		}

		return null;
	}
}

/** Niveau de validation de la directive */
export type StatutValidation = 'ERROR' | 'WARNING';
